import { useEffect, useState } from 'react'
import { Formik, getIn, useFormikContext } from "formik";
import { isEmpty } from "lodash";
import * as yup from 'yup';
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { Autocomplete, Box, Button, CircularProgress, Container, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import Dialog from "components/dialog/dialog";
import type { DailyCheckinUpsertProps, DropdownCategoryListProps } from "modules/types";
import DialogStyle from "components/dialog/style";
import Icon from "components/icon";
import GlobalStyle from "modules/styles";
import { useGetVoucherListDropdownMutation } from "store/tokenRequest";
import { useLazyGetVoucherUsageDetailQuery } from "store/voucherUsage";
import ModalConfirm from "components/modalConfirm/modalConfirm";


const initialDailyCheckin: DailyCheckinUpsertProps & { bannerImage?: string | File | null } = {
  title: '',
  descriptionId: '',
  descriptionEn: '',
  periodStart: '',
  periodEnd: '',
  isStatusActive: false,
  bannerImage: null,
  createdAt: '',
  createdBy: '',
  disabledAt: '',
  modifiedAt: '',
  modifiedBy: '',
  rewards: [],
  auditTrails: [],
};



const DAY_OPTIONS = Array.from({ length: 14 }, (_, i) => i + 1);

const REWARD_TYPE_OPTIONS = ['Voucher', 'Coins'];


const MAX_FILE_SIZE = 200 * 1024; // 200KB

const validationSchema = yup.object({
  title: yup.string().required('Required'),
  descriptionId: yup.string().required('Required'),
  descriptionEn: yup.string().required('Required'),
  periodStart: yup.string().required('Period start is required')
    .test('is-date-valid', 'start date must be earlier than end date', (val, ctx) => dayjs(val).isBefore(ctx.parent.periodEnd)),
  periodEnd: yup.string().required('Period end is required')
    .test('is-date-valid', 'end date must be later than start date', (val, ctx) => dayjs(val).isAfter(ctx.parent.periodStart)),
  bannerImage: yup.mixed().required('Required').test('fileSize', 'File too large (max 200kb)', (value) => {
    if (value instanceof File) {
      return (value as File).size <= MAX_FILE_SIZE;
    } else {
      return true
    }
  }),
  rewards: yup.array().of(
    yup.object().shape({
      day: yup.number()
        .required('Required')
        .oneOf(DAY_OPTIONS, 'Invalid day option') // Enforce valid options
        .test('is-unique', 'Duplicate day selected', function (value) {
          if (!value) return true;
          const rewardDays: typeof initialDailyCheckin['rewards'] = this.from?.[1].value.rewards;
          const count = rewardDays.filter((item) => item.day === value).length;

          return count <= 1;
        }).test({
          name: 'is-ordered',
          message: 'Rewards must be ordered by day',
          test: function () {
            const rewardDays: typeof initialDailyCheckin['rewards'] = this.from?.[1].value.rewards;
            if (!rewardDays) return true;

            const days = rewardDays.map(item => item.day);
            const sortedDays = [...days].sort((a, b) => a - b);

            return JSON.stringify(days) === JSON.stringify(sortedDays);
          }
        }),
      rewardType: yup.string().required('Required').oneOf(REWARD_TYPE_OPTIONS),
      voucherId: yup.number().when('rewardType', {
        is: 'Voucher',
        then: s => s.required('Required'),
        otherwise: s => s.notRequired()
      }),
      coinValue: yup.number().when('rewardType', {
        is: 'Coins',
        then: s => s.required('Required'),
        otherwise: s => s.notRequired()
      }),
      quotaValue: yup.number().when('rewardType', {
        is: 'Voucher',
        then: s => s.required('Required')
        .test('min-quota'
          ,'Quota must be greater than 0'
          , function (value) {
          if (value === undefined || value === null) return true;
          return value > 0;
        })
        .test('max-quota'
          , function (value) {
          if (value === undefined || value === null) return true;
          const { maxAvailable } = this.parent;
          const max = Number(maxAvailable) || 0;
          if (value > max) {
            return this.createError({ message: `Quota cannot exceed available stock ${max}` });
          }
          return true;
        })           
        .test('max-accumulated-quota'
          , function (value) {
          if (value === undefined || value === null) return true;
          const { voucherId } = this.parent;
          if (!voucherId) return true;

          const allRewards: typeof initialDailyCheckin['rewards'] = this.from?.[1].value.rewards || [];
          const totalQuota = allRewards
            .filter((r: typeof initialDailyCheckin['rewards'][number]) => r.rewardType === 'Voucher' && String(r.voucherId) === String(voucherId))
            .reduce((sum: number, r: typeof initialDailyCheckin['rewards'][number]) => sum + (Number(r.quotaValue) || 0), 0);
            const max = Number(this.parent.maxAvailable) || 0;  
            if (totalQuota > max) {
            return this.createError({ message: `Total accumulated quota for this voucher exceeds available stock ${max}` });
          }
          return true;
        }),
        otherwise: s => s.notRequired()
      })
    })
  )
});

export default function DailyCheckinForm({
  onSubmit,
  initialValues = initialDailyCheckin,
  open,
  onClose,
  fetching,
  loading
}: {
  onSubmit: (formData: FormData) => void
    initialValues?: typeof initialDailyCheckin
  open: boolean
  onClose: () => void
  loading: boolean
  fetching?: boolean
  }) {
  const [isConfirm, setIsConfirm] = useState(false);


  function handleSubmit({ bannerImage: BannerImage, ...values }: typeof initialValues) {
    const formData = new FormData();
    formData.set('bannerFile', BannerImage as string)
    formData.set('checkinData', JSON.stringify(values))
    onSubmit(formData);
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validateOnMount={false}
      enableReinitialize>
      {(formik) => {
        const { values, errors } = formik

        const disableAdd = values.rewards.length >= DAY_OPTIONS.length
        const isStatusActive = values.isStatusActive;
        const disableSubmit = isStatusActive ? false : dayjs(initialValues.periodStart).isBefore(Date.now())

        const handleChangeDate = (field: string) => (e: Dayjs | null) => {
          const newDate = (!isEmpty(e) && e?.format('YYYY-MM-DD HH:mm:ss')) || ''
          formik.setFieldValue(field, newDate)
        }

        function handleAddReward() {
          formik.setFieldValue('rewards', [
            ...values.rewards,
              { uuid: Date.now(), day: 1, rewardType: 'Voucher', voucherId: '', coinValue: '', quotaValue: '', maxAvailable: 0 }
          ])
          formik.setFieldError('rewards', undefined)
        }

        return (
          <Dialog
            isDisabled={disableSubmit && !fetching || loading} 
            title={fetching ? 'Loading...' : initialValues.id ? 'Edit Daily Check-In' : 'Create Daily Check-In'}
            open={open}
            onCancel={onClose}
            onSubmit={() => setIsConfirm(true)}
            loading={loading}
            maxWidth='md'
          >
            <ModalConfirm
              open={isConfirm}
              onClose={() => setIsConfirm(false)}
              text='save'
              text2='Daily Check in submission cannot be edited once the period has started.'
              onSubmit={() => {
                formik.validateForm(values).then(res => {
                  if (isEmpty(res)) {
                    setIsConfirm(false)
                    formik.submitForm()
                  } else {
                    setIsConfirm(false)
                  }
                })
              }}
              loading={loading}
              title='Save Daily Checkin'
            />
            <Container {...DialogStyle.Container} sx={{ ...DialogStyle.Container.sx, justifyContent: 'center' }}>
              {fetching && <CircularProgress color='inherit' />}
              {!fetching && (

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ display: 'flex', flex: 1, gap: 2, flexDirection: 'column' }}>
                    <TextField
                      id='title'
                      variant='outlined'
                      label='Daily Checkin Name'
                      name="title"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={values.title || ''}
                      error={Boolean(errors?.title)}
                      helperText={errors?.title}
                      fullWidth
                       disabled={isStatusActive}
                    />
                    <TextField
                      id='descriptionId'
                      variant='outlined'
                      label='Description ID'
                      name="descriptionId"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={values.descriptionId || ''}
                      error={Boolean(errors?.descriptionId)}
                      helperText={errors?.descriptionId}
                      fullWidth
                       disabled={isStatusActive}
                    />
                    <TextField
                      id='descriptionEn'
                      variant='outlined'
                      label='Description EN'
                      name="descriptionEn"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={values.descriptionEn || ''}
                      error={Boolean(errors?.descriptionEn)}
                      helperText={errors?.descriptionEn}
                      fullWidth
                       disabled={isStatusActive}
                    />
                    <Grid item xs={12}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
                      <DateTimePicker
                        value={values.periodStart ? dayjs(values.periodStart) : null}
                        onChange={handleChangeDate('periodStart')}
                        minDate={!isStatusActive ? dayjs() : dayjs(values.periodStart)} 
                        onClose={() => {
                          formik.handleBlur({ target: { name: 'periodStart' } })
                        }}
                        label='Period Start Date'
                        format='DD-MM-YYYY HH:mm'
                        sx={{ width: '100%' }}
                        disabled={isStatusActive}
                        slotProps={{ textField: errors?.periodStart ? { error: true, helperText: errors.periodStart } : undefined }}
                      />
                    </LocalizationProvider>
                    </Grid>
                    <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
                      <DateTimePicker
                        value={values.periodEnd ? dayjs(values.periodEnd) : null}
                        onChange={handleChangeDate('periodEnd')}
                        onClose={() => {
                          formik.handleBlur({ target: { name: 'periodEnd' } })
                        }}
                        label='Period End Date'
                        format='DD-MM-YYYY HH:mm'
                        minDate={values.periodStart ? dayjs(values.periodStart) : undefined}
                        sx={{ width: '100%' }}
                        slotProps={{ textField: errors?.periodEnd ? { error: Boolean(errors?.periodEnd), helperText: errors?.periodEnd } : undefined }}
                      />
                    </LocalizationProvider>
                    <TextField
                      id='bannerImage'
                      label='Banner Image'
                      type='file'
                      placeholder='Image'
                      InputLabelProps={{ shrink: true }}
                      disabled={isStatusActive}
                      inputProps={{ accept: 'image/png, image/gif, image/jpeg' }}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (isStatusActive) return; 
                        formik.setFieldValue('bannerImage', e.target.files?.[0])
                      }}
                      onBlur={formik.handleBlur}
                      fullWidth
                      helperText={errors?.bannerImage as string}
                      error={!isEmpty(errors?.bannerImage)}
                    />

                    {values.bannerImage && (
                      <Grid item xs={12}>
                        <Typography>Banner Image</Typography>
                        <Box display='flex' justifyContent='center' alignItems='center'>
                          <img
                            src={typeof values.bannerImage === 'object' ? URL.createObjectURL(values.bannerImage) : values.bannerImage}
                            alt='Banner Image'
                            width="100%"
                          />
                        </Box>
                      </Grid>
                    )}

                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell width='15%'>Check In</TableCell>
                          <TableCell>Reward Type</TableCell>
                          <TableCell width='30%'>Value</TableCell>
                          <TableCell>Quota</TableCell>
                          <TableCell/>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {values.rewards.map((reward, index) => (
                          <RewardRow
                            key={reward.uuid}
                            reward={reward}
                            index={index}
                            isStatusActive={isStatusActive}
                          />
                        ))}
                      </TableBody>
                    </Table>
                    <Box>
                      <Button variant="outlined" onClick={handleAddReward} disabled={disableAdd || isStatusActive}>
                        Add Reward
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </Container>
          </Dialog>
        )
      }}
    </Formik>
  )
}

function RewardRow(
  {
    reward,
    index,
    isStatusActive,
  }: {
      reward: typeof initialDailyCheckin['rewards'][number],
      index: number,
      isStatusActive: boolean 
  }
) {
  const formik = useFormikContext<typeof initialDailyCheckin>()
  

  const { values, errors } = formik

  const [getVoucherListDropdown, voucherDropdown] = useGetVoucherListDropdownMutation({
    fixedCacheKey: 'getVoucherListDropdown',
  })
  const [getVoucherUsageDetail] = useLazyGetVoucherUsageDetailQuery()

  useEffect(() => {
    if (reward.voucherId && !voucherDropdown.data) {
      getVoucherListDropdown()
    }
  }, [voucherDropdown.data, reward.voucherId])

  useEffect(() => {
    if (reward.voucherId && reward.rewardType === 'Voucher') {
      getVoucherUsageDetail(Number(reward.voucherId))
        .unwrap()
        .then((res) => {
          formik.setFieldValue(`rewards[${index}].maxAvailable`, res.available);
        });
    }
  }, [reward.voucherId]);

  const initialVoucher = voucherDropdown?.data?.find((item: DropdownCategoryListProps) => item.id === reward.voucherId)

  const handleDeleteReward = () => {
    formik.setFieldValue('rewards', values.rewards.filter((_, i) => i !== index))
    formik.setFieldError('rewards', undefined)
  }

  return <TableRow key={reward.uuid}>
    <TableCell sx={{ verticalAlign: 'top' }}>
      <FormControl fullWidth>
        <InputLabel id='Day'>Check In</InputLabel>
        <Select
          labelId='day'
          id='day'
          value={reward.day || ''}
          name={`rewards[${index}].day`}
          onChange={e => formik.setFieldValue(`rewards[${index}].day`, e.target.value)}
          onBlur={formik.handleBlur}
          error={Boolean(getIn(errors, `rewards[${index}].day`))}
          label='Day'
          disabled={isStatusActive}
        >
          {DAY_OPTIONS.map((item) => ( 
            <MenuItem value={item} key={item}>
              {item}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText error={Boolean(getIn(errors, `rewards[${index}].day`))}>
          {getIn(errors, `rewards[${index}].day`)}
        </FormHelperText>
      </FormControl>
    </TableCell>
    <TableCell sx={{ verticalAlign: 'top' }}>
      <FormControl fullWidth>
        <InputLabel id='Reward Type'>Reward Type</InputLabel>
        <Select
          labelId='Reward Type'
          id='Reward Type'
          value={reward.rewardType || ''}
          name={`rewards[${index}].rewardType`}
          onChange={e => { 
            formik.setFieldValue(`rewards[${index}]`, { ...reward, rewardType: e.target.value, voucherId: '', coinValue: '' })
          }}
          onBlur={formik.handleBlur}
          error={Boolean(getIn(errors, `rewards[${index}].rewardType`))}
          label='Reward Type'
          disabled={isStatusActive}
        >
          {REWARD_TYPE_OPTIONS.map((item) => (
            <MenuItem value={item} key={item} disabled={item === 'Coins'}>
              {item}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText error={Boolean(getIn(errors, `rewards[${index}].rewardType`))}>
          {getIn(errors, `rewards[${index}].rewardType`)}
        </FormHelperText>
      </FormControl>
    </TableCell>
    <TableCell sx={{ verticalAlign: 'top' }}>
      {reward.rewardType === 'Coins' ? (
        <TextField
          id={`value-${index}`}
          variant='outlined'
          label='Coins Amount'
          name={`rewards[${index}].coinValue`}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={reward.coinValue || ''}
          error={Boolean(getIn(errors, `rewards[${index}].coinValue`))}
          disabled={isStatusActive}
          fullWidth />
      ) : reward.rewardType === 'Voucher' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Autocomplete
            key={initialVoucher?.id ? `${voucherDropdown.isLoading}` : undefined}
            options={voucherDropdown.data || []}
            getOptionLabel={(list) => list.name}
            isOptionEqualToValue={(option, value) =>
              option && value ? option.id == value.id : false
            }
            onOpen={() => !voucherDropdown.data && getVoucherListDropdown()} 
            onChange={(_, option) => {
              formik.setFieldValue(`rewards[${index}].voucherId`, option?.id);
              if (option?.id) {
                getVoucherUsageDetail(Number(option.id))
                  .unwrap()
                  .then((res) => {
                    formik.setFieldValue(`rewards[${index}].maxAvailable`, res.available);
                    formik.setFieldValue(`rewards[${index}].quotaValue`, res.available);
                  })
                  .catch(() => {
                    formik.setFieldValue(`rewards[${index}].maxAvailable`, 0);
                    formik.setFieldValue(`rewards[${index}].quotaValue`, 0);
                  });
              } else {
                formik.setFieldValue(`rewards[${index}].quotaValue`, 0);
                formik.setFieldValue(`rewards[${index}].maxAvailable`, 0);
              }
            }}
            ListboxProps={GlobalStyle.ListBox}
            renderOption={(props, item) => (
              <li {...props} key={item.id}>
                {item.name}
              </li>
            )}
            value={initialVoucher}
            loading={voucherDropdown.isLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                name={`rewards[${index}].voucherId`}
                label='Voucher Name'
                error={Boolean(errors?.rewards?.[index])}
                helperText={getIn(errors, `rewards[${index}].voucherId`)}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {voucherDropdown.isLoading && (
                        <CircularProgress color='inherit' size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />     
        </Box>
      ) : null}
    </TableCell>
    <TableCell sx={{ verticalAlign: 'top' }}>
      {reward.rewardType === 'Voucher' && (
        <TextField
            id={`quota-${index}`}
            variant='outlined'
            label='Quota'
            name={`rewards[${index}].quotaValue`}
            value={reward.quotaValue ?? 0} // Quota is editable
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(getIn(errors, `rewards[${index}].quotaValue`))}
            helperText={getIn(errors, `rewards[${index}].quotaValue`)}
            fullWidth
          />
      )}
    </TableCell>
    <TableCell sx={{ verticalAlign: 'top' }}>
      <Button onClick={handleDeleteReward} disabled={isStatusActive}><Icon icon="Delete" /></Button> 
    </TableCell>
  </TableRow>
}